import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'

import MicRecorder from '../AudioRecorder/MicRecorder'
import SettingsButton from './SettingsButton'
import { CustomAudioControls } from '../AudioPlayer'
import { MediaPlayerMutexContext } from '../../contexts/MediaPlayerMutexContext'
import { useSettingsStore } from '../../stores/settings'
import { originalAudioHonored } from '../AudioRecorder/recorderLogic'
import { getLogger } from '@deltachat-desktop/shared/logger'
import styles from './styles.module.scss'

const log = getLogger('renderer/MicrophoneTest')

/** Long enough to say a sentence, short enough that nobody waits for it. */
const TAKE_MS = 5000

/**
 * Below this the take is silence: MicRecorder opens the mic 300ms before it
 * starts feeding the encoder, so a stop inside that window still yields a blob
 * (Encoder.finish() always appends its flush block) that plays as 0:00. At the
 * 32kbps this records at, 1KiB is about a quarter second.
 */
const MIN_TAKE_BYTES = 1024

type Phase = 'idle' | 'recording' | 'done'

/**
 * Record a few seconds and play them back, so "record without noise filtering"
 * can be judged by ear instead of by its description.
 *
 * Deliberately NOT live monitoring: routing the mic to the speaker is what
 * causes the howl, and the mitigation every app relies on for that is echo
 * cancellation — the very thing this setting turns off. Record-then-play
 * answers the same question with no feedback path at all.
 *
 * ponytail: no A/B of filtered vs unfiltered in one take. The processing is
 * applied by the OS during capture, so it can't be synthesised client-side and
 * two differently-constrained tracks off one device aren't portable. Toggling
 * the switch and recording again is the comparison; keeping the previous take
 * around to play side by side is the upgrade if that turns out to be too
 * fiddly to judge.
 */
export default function MicrophoneTest() {
  const settings = useSettingsStore()[0]?.desktopSettings
  // Mirror AudioRecorder's own gate exactly (`experimental && originalAudio`,
  // AudioRecorder.tsx). Recording unfiltered here while real voice messages
  // stay filtered would make this a test of something the app doesn't do.
  const experimental = settings?.experimentalAudioPlayerControls === true
  const originalAudio =
    experimental && settings?.voiceRecordingOriginalAudio === true
  const deviceId = settings?.voiceRecordingDeviceId ?? null
  const mutexCtx = useContext(MediaPlayerMutexContext)

  const [phase, setPhase] = useState<Phase>('idle')
  const [url, setUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  // Whether the mic/browser actually honored the unprocessed request.
  // null = it didn't say (keys absent from getSettings), so we can't claim either way.
  const [honored, setHonored] = useState<boolean | null>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const recorderRef = useRef<MicRecorder | null>(null)
  const timerRef = useRef<number | null>(null)
  // Bumped on every start and on every stop. An in-flight start reads its
  // generation before awaiting the permission prompt and closes the stream it
  // opened if it has been superseded since — otherwise stopping while the
  // prompt is up orphans the mic: MicRecorder.stop() is gated on a
  // processor/microphone that only exist once the stream opens, so it no-ops,
  // and the recording indicator stays lit for the rest of the session.
  const startGenRef = useRef(0)
  // Revoked on replace and on unmount; also guards the async stop path from
  // writing state into an unmounted component.
  const urlRef = useRef<string | null>(null)
  const aliveRef = useRef(true)

  const release = useCallback(() => {
    if (timerRef.current != null) {
      window.clearTimeout(timerRef.current)
      timerRef.current = null
    }
    if (urlRef.current != null) {
      URL.revokeObjectURL(urlRef.current)
      urlRef.current = null
    }
  }, [])

  useEffect(() => {
    aliveRef.current = true
    return () => {
      aliveRef.current = false
      // A take still running when the user leaves the page must not keep the
      // microphone open. No generation bump needed: an in-flight start checks
      // aliveRef too, and closes the stream it opened when it resolves.
      recorderRef.current?.stop()
      recorderRef.current = null
      release()
    }
  }, [release])

  // One sound at a time: the test clip and the global voice-message player
  // each pause the other, the same arrangement the recorder's preview row has
  // with the singleton.
  useEffect(() => {
    const singleton = mutexCtx.audioElement
    const el = audioRef.current
    if (url == null || el == null || singleton == null) {
      return
    }
    const onTestPlay = () => {
      if (!singleton.paused) {
        singleton.pause()
      }
    }
    const onSingletonPlay = () => {
      if (!el.paused) {
        el.pause()
      }
    }
    el.addEventListener('play', onTestPlay)
    singleton.addEventListener('play', onSingletonPlay)
    return () => {
      el.removeEventListener('play', onTestPlay)
      singleton.removeEventListener('play', onSingletonPlay)
    }
  }, [url, mutexCtx.audioElement])

  const finish = useCallback(async () => {
    const rec = recorderRef.current
    if (rec == null) {
      return
    }
    recorderRef.current = null
    startGenRef.current++ // invalidate a start still waiting on the prompt
    // Read the applied constraints BEFORE stop() tears the track down.
    const applied = rec.getAppliedSettings()
    if (applied == null) {
      // Stopped before the mic ever opened: nothing was captured, and the
      // superseded start() will close the stream when it resolves.
      setPhase('idle')
      return
    }
    try {
      const [buffer] = await rec.stop().getMp3()
      if (!aliveRef.current) {
        return
      }
      const blob = new Blob(buffer as any, { type: 'audio/mp3' })
      if (blob.size < MIN_TAKE_BYTES) {
        setPhase('idle')
        setError('That take was too short to hear — give it a second or two.')
        return
      }
      const next = URL.createObjectURL(blob)
      release()
      urlRef.current = next
      setUrl(next)
      setHonored(originalAudioHonored(applied as any))
      setPhase('done')
    } catch (err: any) {
      log.error('test recording failed', err)
      if (aliveRef.current) {
        setPhase('idle')
        setError('Could not process the recording.')
      }
    }
  }, [release])

  const start = useCallback(async () => {
    if (phase === 'recording') {
      // Second click ends the take early rather than starting a second one.
      finish()
      return
    }
    release()
    setUrl(null)
    setHonored(null)
    setError(null)
    const gen = ++startGenRef.current
    const rec = new MicRecorder(() => {}, {
      bitRate: 32,
      originalAudio,
      deviceId,
    })
    recorderRef.current = rec
    setPhase('recording')
    try {
      await rec.start()
    } catch (err: any) {
      log.error('could not open the microphone', err)
      if (gen === startGenRef.current) {
        recorderRef.current = null
      }
      if (aliveRef.current) {
        setPhase('idle')
        setError(
          'Could not use the microphone. Check that the browser is allowed to access it.'
        )
      }
      return
    }
    // Stopped or unmounted while the permission prompt was up. The stream is
    // open now, so this stop() actually closes it.
    if (gen !== startGenRef.current || !aliveRef.current) {
      rec.stop()
      return
    }
    timerRef.current = window.setTimeout(finish, TAKE_MS)
  }, [phase, originalAudio, deviceId, finish, release])

  const label =
    phase === 'recording' ? 'Recording… tap to stop' : 'Test the microphone'

  return (
    <>
      <SettingsButton onClick={start} dataTestid='mic-test-button'>
        {label}
      </SettingsButton>
      {error != null && (
        <div className={styles.micTestResult}>
          <div className={styles.settingsRowDescription}>{error}</div>
        </div>
      )}
      {phase === 'done' && url != null && (
        <div className={styles.micTestResult} data-testid='mic-test-result'>
          <audio ref={audioRef} src={url} preload='metadata' />
          <CustomAudioControls audioRef={audioRef} src={url} compact />
          {/* Only meaningful when unfiltered was actually asked for — with the
              switch off there is no request for the browser to honor. */}
          {originalAudio && (
            <div className={styles.settingsRowDescription}>
              {honored === false
                ? 'This browser or microphone ignored the request and filtered the sound anyway.'
                : honored === true
                  ? 'Recorded unfiltered, as requested.'
                  : "This browser did not say whether it filtered the sound, so it can't be confirmed either way."}
            </div>
          )}
        </div>
      )}
    </>
  )
}
